import type {GridFilterItem, GridFilterModel, GridLinkOperator} from "@mui/x-data-grid-pro";

/**
 * API filter condition shape.
 * Aligned with improve-service filter-helpers and shapeshift IEntityFilter:
 * { operator: "and"|"or", conditions: [{ operator, attribute, value }] }
 * - attribute: column/field name
 * - operator: short codes only (co, eq, ne, sw, ew, pr, gt, ge, lt, le, ie, ine, is)
 * - value: raw value (not URL-encoded; backend uses as-is)
 */
export interface TeamProgressFilterCondition {
	operator: string;
	attribute: string;
	value: string;
}

/**
 * MUI Data Grid sends operator values from GridFilterOperator (e.g. VerboseOperator).
 * Map those to short codes expected by improve-service filter-helpers and improve-api.
 * @see https://mui.com/x/react-data-grid/filtering/#filter-operators
 */
const OPERATOR_SHORT_CODES: Record<string, string> = {
	equals: "eq",
	doesnotequal: "ne",
	contains: "co",
	doesnotcontain: "ne",
	startswith: "sw",
	endswith: "ew",
	isempty: "ie",
	isnotempty: "ine",
	lessthan: "lt",
	greaterthan: "gt",
	lessthanorequal: "le",
	greaterthanorequal: "ge",
	is: "is",
	"is any of": "in",
	not: "ne",
	after: "gt",
	before: "lt"
};

/**
 * Normalize MUI Data Grid operator (e.g. "contains", "equals") to short codes
 * expected by improve-service filter-helpers and improve-api (co, eq, ne, sw, ew, etc.).
 */
function normalizeOperator(operator: string): string {
	if (!operator || typeof operator !== "string") return "co";
	const op = operator.toLowerCase().trim();
	return OPERATOR_SHORT_CODES[op] ?? op;
}

/** Operators that do not require a value (is empty, is not empty, present). */
const VALUE_LESS_OPERATORS = new Set(["ie", "ine", "pr"]);

function conditionHasValue(item: GridFilterItem): boolean {
	const normalized = normalizeOperator((item.operatorValue as string) ?? "co");
	if (VALUE_LESS_OPERATORS.has(normalized)) return true;
	const v = item.value;
	if (v === undefined || v === null) return false;
	if (Array.isArray(v)) return v.length > 0;
	return String(v).trim() !== "";
}

function serializeFilterValue(value: unknown): string {
	if (Array.isArray(value)) return value.join(",");
	return value != null ? String(value) : "";
}

/**
 * Converts MUI DataGrid filter model to the API filters format.
 * Aligned with improve-service filter-helpers: same shape and short operator codes; value is raw.
 */
export function gridFilterModelToApiFilters(filterModel: GridFilterModel): string | undefined {
	if (!filterModel.items?.length) {
		return undefined;
	}
	const conditions: TeamProgressFilterCondition[] = filterModel.items
		.filter((item: GridFilterItem) => conditionHasValue(item))
		.map((item: GridFilterItem) => {
			const operator = normalizeOperator((item.operatorValue as string) ?? "co");
			return {
				operator,
				attribute: String(item.columnField ?? ""),
				value: serializeFilterValue(item.value)
			};
		});
	if (conditions.length === 0) {
		return undefined;
	}
	return JSON.stringify({
		operator: filterModel.linkOperator ?? ("and" as GridLinkOperator),
		conditions
	});
}

/**
 * Converts GridFilterModel to the conditions array shape used by export (handleUpdatedFilter).
 * Uses same operator normalization as API filters (short codes) for consistency with improve-service.
 */
export function gridFilterModelToExportConditions(filterModel: GridFilterModel): {
	operator: GridLinkOperator;
	conditions: { field: string; operator: string; value: unknown }[];
} {
	const defaultOperator = "and" as GridLinkOperator;
	if (!filterModel.items?.length) {
		return {operator: filterModel.linkOperator ?? defaultOperator, conditions: []};
	}
	const conditions = filterModel.items
		.filter((item: GridFilterItem) => conditionHasValue(item))
		.map((item: GridFilterItem) => ({
			field: String(item.columnField ?? ""),
			operator: normalizeOperator((item.operatorValue as string) ?? "co"),
			value: item.value
		}));
	return {
		operator: filterModel.linkOperator ?? defaultOperator,
		conditions
	};
}
